import React, { useEffect, useState } from "react";
import { api, formatApiError, todayISO, DAY_NAMES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Calendar, Copy, ArrowsClockwise, FloppyDisk, Stack, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { toastWithUndo } from "@/lib/dayUndo";

/**
 * Tab pill — defined at module scope to avoid React tree thrash.
 */
function TabBtn({ value, icon: Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-1.5 sm:px-3 py-2.5 rounded-xl text-[10px] sm:text-xs whitespace-nowrap font-semibold uppercase tracking-[0.04em] sm:tracking-[0.08em] inline-flex items-center justify-center gap-1.5 transition-colors ${
        active ? "bg-ink text-bone" : "bg-bone text-ink-2 hover:bg-muted2"
      }`}
      data-testid={`day-actions-tab-${value}`}
    >
      <Icon size={14} weight={active ? "fill" : "regular"} />
      {label}
    </button>
  );
}

/**
 * Whole-day actions: move/copy this day's blocks to another date or athlete,
 * save the day as a reusable template, or stamp a saved template back onto
 * this day. Mirrors MoveBlockDialog patterns one level up.
 */
export default function DayActionsDialog({
  programId,
  dayIdx,
  blockCount,
  athleteId,
  trigger,
  onDone,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("move"); // move | save | apply
  const [busy, setBusy] = useState(false);

  // Move/copy state
  const [mode, setMode] = useState("copy");
  const [toDate, setToDate] = useState(() => todayISO());
  const [toAthleteId, setToAthleteId] = useState(athleteId);
  const [athletes, setAthletes] = useState([]);

  // Save-template state
  const [tplName, setTplName] = useState("");

  // Apply-template state
  const [templates, setTemplates] = useState([]);
  const [applyTplId, setApplyTplId] = useState("");
  const [applyMode, setApplyMode] = useState("append");

  useEffect(() => {
    if (!open) return;
    if (athletes.length === 0) {
      api.get("/users", { params: { role: "athlete" } })
        .then((r) => setAthletes(r.data))
        .catch(() => {});
    }
    if (templates.length === 0) {
      api.get("/day-templates")
        .then((r) => setTemplates(r.data))
        .catch(() => {});
    }
    setToAthleteId(athleteId);
  }, [open, athleteId, athletes.length, templates.length]);

  const refreshTemplates = async () => {
    try {
      const { data } = await api.get("/day-templates");
      setTemplates(data);
    } catch { /* ignore */ }
  };

  const close = () => { setOpen(false); setBusy(false); };

  const submitMove = async (e) => {
    e.preventDefault();
    if (!toDate) { toast.error("Pick a target date"); return; }
    if (blockCount === 0) { toast.error("Day has no sections to move"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/programs/${programId}/days/${dayIdx}/move`,
        { to_date: toDate, mode, to_athlete_id: toAthleteId !== athleteId ? toAthleteId : null },
      );
      const verb = mode === "copy" ? "Copied" : "Moved";
      const where = toAthleteId && toAthleteId !== athleteId
        ? (athletes.find((a) => a.id === toAthleteId)?.name || "another athlete")
        : "another date";
      toast.success(`${verb} ${data.moved_count} section${data.moved_count === 1 ? "" : "s"} to ${where} · ${toDate}`);
      close();
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const submitSave = async (e) => {
    e.preventDefault();
    const name = tplName.trim();
    if (!name) { toast.error("Name your template"); return; }
    if (blockCount === 0) { toast.error("Day has no sections to save"); return; }
    setBusy(true);
    try {
      await api.post("/day-templates", { name, program_id: programId, day_index: dayIdx });
      toast.success(`Saved "${name}" template`);
      setTplName("");
      await refreshTemplates();
      setTab("apply");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const submitApply = async (e) => {
    e.preventDefault();
    if (!applyTplId) { toast.error("Pick a template"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/programs/${programId}/days/${dayIdx}/apply-template`,
        { template_id: applyTplId, mode: applyMode },
      );
      toast.success(`Stamped ${data.stamped_count} section${data.stamped_count === 1 ? "" : "s"} onto ${DAY_NAMES[dayIdx]}`);
      close();
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const submitDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/programs/${programId}/days/${dayIdx}`);
      toastWithUndo(
        `Cleared ${DAY_NAMES[dayIdx]} · ${data.removed_count} section${data.removed_count === 1 ? "" : "s"} deleted`,
        programId, data.previous, () => onDone?.(),
      );
      close();
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/day-templates/${tpl.id}`);
      setTemplates((rows) => rows.filter((r) => r.id !== tpl.id));
      if (applyTplId === tpl.id) setApplyTplId("");
      toast.success("Template deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const TabBtnRow = (
    <div className="flex gap-1">
      <TabBtn value="move" icon={ArrowsClockwise} label="Move/Copy" active={tab === "move"} onClick={() => setTab("move")} />
      <TabBtn value="save" icon={FloppyDisk} label="Save" active={tab === "save"} onClick={() => setTab("save")} />
      <TabBtn value="apply" icon={Stack} label="Apply" active={tab === "apply"} onClick={() => setTab("apply")} />
      <TabBtn value="delete" icon={Trash} label="Delete" active={tab === "delete"} onClick={() => setTab("delete")} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-2xl max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Calendar size={18} weight="fill" className="text-moss" />
            {DAY_NAMES[dayIdx]} actions
          </DialogTitle>
          <DialogDescription>
            Move, copy, save, stamp, or delete the whole day&rsquo;s programming in one shot.
          </DialogDescription>
        </DialogHeader>

        {TabBtnRow}

        {tab === "move" && (
          <form onSubmit={submitMove} className="space-y-4" data-testid="day-actions-move-form">
            <div>
              <Label className="hs-label">Action</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMode("move")}
                  className={`p-3 rounded-xl border-2 text-left transition-colors ${
                    mode === "move" ? "border-moss bg-sage/40" : "border-line hover:border-line/80"
                  }`}
                  data-testid="day-move-mode-move"
                >
                  <div className="flex items-center gap-1.5">
                    <ArrowsClockwise size={16} className="text-moss" />
                    <span className="font-semibold text-sm">Move</span>
                  </div>
                  <p className="text-[11px] text-ink-3 mt-1">Reschedule — source day clears.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("copy")}
                  className={`p-3 rounded-xl border-2 text-left transition-colors ${
                    mode === "copy" ? "border-terracotta bg-terracotta/10" : "border-line hover:border-line/80"
                  }`}
                  data-testid="day-move-mode-copy"
                >
                  <div className="flex items-center gap-1.5">
                    <Copy size={16} className="text-terracotta" weight="fill" />
                    <span className="font-semibold text-sm">Copy</span>
                  </div>
                  <p className="text-[11px] text-ink-3 mt-1">Duplicate — source stays.</p>
                </button>
              </div>
            </div>

            <div>
              <Label className="hs-label">Target date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-2 h-11 rounded-xl font-data"
                data-testid="day-move-to-date"
                required
              />
            </div>

            <div>
              <Label className="hs-label">Athlete</Label>
              <Select value={toAthleteId} onValueChange={setToAthleteId}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="day-move-to-athlete">
                  <SelectValue placeholder="Pick an athlete" />
                </SelectTrigger>
                <SelectContent>
                  {athletes.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.id === athleteId ? `${a.name} (this athlete)` : a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close} className="rounded-xl">Cancel</Button>
              <Button
                type="submit"
                disabled={busy || blockCount === 0}
                className={`rounded-xl text-white hover:text-white ${
                  mode === "copy" ? "bg-terracotta hover:bg-terracotta-hover" : "bg-moss hover:bg-moss-hover"
                }`}
                data-testid="day-move-submit"
              >
                {busy ? "Saving…" : mode === "copy" ? "Copy day" : "Move day"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {tab === "delete" && (
          <div className="space-y-4" data-testid="day-actions-delete-panel">
            <div className="rounded-xl border border-terracotta/40 bg-terracotta/10 p-4">
              <div className="flex items-center gap-2 font-semibold text-sm text-terracotta">
                <Trash size={16} weight="fill" /> Delete entire {DAY_NAMES[dayIdx]}
              </div>
              <p className="text-[12px] text-ink-2 mt-1.5">
                Removes all {blockCount} section{blockCount === 1 ? "" : "s"} and day notes. You&rsquo;ll have 10 seconds to undo.
                Logged results in History and the coach&rsquo;s journal are kept.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close} className="rounded-xl">Cancel</Button>
              <Button
                type="button"
                onClick={submitDelete}
                disabled={busy || !programId}
                className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
                data-testid="day-delete-submit"
              >
                <Trash size={14} className="mr-1.5" weight="fill" />
                {busy ? "Deleting…" : "Delete day"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {tab === "save" && (
          <form onSubmit={submitSave} className="space-y-4" data-testid="day-actions-save-form">
            <div>
              <Label className="hs-label">Template name</Label>
              <Input
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                placeholder="Heavy back squat day"
                className="mt-2 h-11 rounded-xl"
                data-testid="day-save-name"
                autoFocus
                required
              />
              <p className="text-[11px] text-ink-3 mt-1">
                Snapshots this day&rsquo;s {blockCount} section{blockCount === 1 ? "" : "s"} as a reusable template.
                Stamp it onto any other day from the Apply tab.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close} className="rounded-xl">Cancel</Button>
              <Button
                type="submit"
                disabled={busy || blockCount === 0}
                className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
                data-testid="day-save-submit"
              >
                <FloppyDisk size={14} className="mr-1.5" weight="fill" />
                {busy ? "Saving…" : "Save template"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {tab === "apply" && (
          <form onSubmit={submitApply} className="space-y-4" data-testid="day-actions-apply-form">
            {templates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line p-6 text-center">
                <Stack size={28} className="mx-auto text-ink-3" />
                <p className="mt-2 text-sm text-ink-2">No saved day templates yet.</p>
                <p className="text-[11px] text-ink-3 mt-1">
                  Build a day, then save it via the Save tab — it&rsquo;ll show up here.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <Label className="hs-label">Pick a template</Label>
                  <ul className="mt-2 divide-y divide-line max-h-56 overflow-y-auto rounded-xl border border-line">
                    {templates.map((t) => (
                      <li
                        key={t.id}
                        className={`flex items-center justify-between gap-2 px-3 py-2.5 cursor-pointer ${
                          applyTplId === t.id ? "bg-sage/40" : "hover:bg-muted2"
                        }`}
                        onClick={() => setApplyTplId(t.id)}
                        data-testid={`day-apply-tpl-${t.name}`}
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{t.name}</div>
                          <div className="text-[11px] text-ink-3">
                            {(t.blocks || []).length} section{(t.blocks || []).length === 1 ? "" : "s"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); deleteTemplate(t); }}
                          className="shrink-0 p-1.5 rounded-lg text-ink-3 hover:text-terracotta hover:bg-terracotta/10"
                          title="Delete template"
                          data-testid={`day-apply-tpl-delete-${t.name}`}
                        >
                          <Trash size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <Label className="hs-label">Mode</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setApplyMode("append")}
                      className={`p-3 rounded-xl border-2 text-left transition-colors ${
                        applyMode === "append" ? "border-moss bg-sage/40" : "border-line hover:border-line/80"
                      }`}
                      data-testid="day-apply-mode-append"
                    >
                      <div className="font-semibold text-sm">Append</div>
                      <p className="text-[11px] text-ink-3 mt-1">Adds to what&rsquo;s already there.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setApplyMode("replace")}
                      className={`p-3 rounded-xl border-2 text-left transition-colors ${
                        applyMode === "replace" ? "border-terracotta bg-terracotta/10" : "border-line hover:border-line/80"
                      }`}
                      data-testid="day-apply-mode-replace"
                    >
                      <div className="font-semibold text-sm">Replace</div>
                      <p className="text-[11px] text-ink-3 mt-1">Clears the day first.</p>
                    </button>
                  </div>
                </div>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close} className="rounded-xl">Cancel</Button>
              <Button
                type="submit"
                disabled={busy || !applyTplId}
                className={`rounded-xl text-white hover:text-white ${
                  applyMode === "replace" ? "bg-terracotta hover:bg-terracotta-hover" : "bg-moss hover:bg-moss-hover"
                }`}
                data-testid="day-apply-submit"
              >
                {busy ? "Saving…" : applyMode === "replace" ? "Replace day" : "Stamp onto day"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
